import React, { useState, useEffect, FormEvent } from 'react';
import {
    Drawer,
    Button,
    TextInput,
    Select,
    NumberInput,
    Textarea,
    Notification,
    Group,
    Title,
    Box,
    Loader
} from '@mantine/core';
import axios from 'axios';
import { getToken } from '../../../auth/service/authService';
import { BackendUrl } from '../../../util/config';
import {RentContract} from "../../../models/RentContract";
import {DateInput} from "@mantine/dates";





interface EditRentContractProps {
    rentContractId: number;
    organisationId: string;
    onClose: () => void;
    onUpdated: () => void;
}

export const EditRentContract: React.FC<EditRentContractProps> = ({
                                                                      rentContractId,
                                                                      onClose,
                                                                      onUpdated
                                                                  }) => {

    const [opened, setOpened] = useState(false);

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [description, setDescription] = useState('');
    const [monthlyPrice, setMonthlyPrice] = useState<number>(0);

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);



    // Fetch existing contract on open
    useEffect(() => {
        if (!opened) return;
        const fetchContract = async () => {
            setInitialLoading(true);
            try {
                const token = getToken();
                const resp = await axios.get<RentContract>(
                    `${BackendUrl}/rent-contracts/${rentContractId}`,
                    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
                );
                const c = resp.data;
                setFirstName(c.firstName);
                setLastName(c.lastName);
                setEmail(c.email);
                setPhoneNumber(c.phoneNumber);
                setDescription(c.description || '');
                setMonthlyPrice(c.monthlyPrice);
                setErrorMessage(null);
                // In fetchContract useEffect
                setStartDate(new Date(c.startDate));
                setEndDate(new Date(c.endDate));

            } catch (err: any) {
                console.error(err);
                setErrorMessage(err.response?.data || 'Fehler beim Laden des Vertrags');
            } finally {
                setInitialLoading(false);
            }
        };

        fetchContract();
    }, [opened, rentContractId]);


    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        setSuccessMessage(null);


        setLoading(true);
        try {
            const token = getToken();
            // Build payload in RentContract shape
            const payload: Partial<RentContract> = {
                firstName,
                lastName,
                email,
                phoneNumber,
                description,
                monthlyPrice,
                startDate: startDate?.toISOString().split('T')[0],
                endDate: endDate?.toISOString().split('T')[0],
            };
            await axios.put(
                `${BackendUrl}/rent-contracts/${rentContractId}`,
                payload,
                { headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' } }
            );
            setSuccessMessage('Mietvertrag erfolgreich aktualisiert!');
            onUpdated();
            onClose();
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err.response?.data || 'Fehler beim Aktualisieren des Vertrags');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="filled" onClick={() => setOpened(true)}>
                Bearbeiten
            </Button>
            <Drawer opened={opened} onClose={() => setOpened(false)} title="Bearbeite Mietvertrag" padding="md" size="lg">
                {initialLoading ? (
                    <Box>
                        <Loader />
                    </Box>
                ) : (
                    <>
                        <Box mb="sm">
                            {successMessage && <Notification color="teal" onClose={() => setSuccessMessage(null)}>{successMessage}</Notification>}
                            {errorMessage && <Notification color="red" onClose={() => setErrorMessage(null)}>{errorMessage}</Notification>}
                        </Box>
                        <form onSubmit={handleSubmit}>
                            <Title order={4} mb="md">
                                Mietvertragsdaten bearbeiten
                            </Title>
                            <DateInput
                                label="Startdatum"
                                required
                                value={startDate}
                                onChange={setStartDate}
                                mb="sm"
                            />
                            <DateInput
                                label="Enddatum"
                                required
                                value={endDate}
                                onChange={setEndDate}
                                mb="sm"
                            />
                            <NumberInput label="Monatlicher Preis" placeholder="100" required value={monthlyPrice} onChange={val => setMonthlyPrice(val as number ?? 0)} mb="sm" />
                            <TextInput label="Vorname" placeholder="Max" required value={firstName} onChange={e => setFirstName(e.currentTarget.value)} mb="sm" />
                            <TextInput label="Nachname" placeholder="Mustermann" required value={lastName} onChange={e => setLastName(e.currentTarget.value)} mb="sm" />
                            <TextInput label="Email" placeholder="max.mustermann@example.com" required value={email} onChange={e => setEmail(e.currentTarget.value)} mb="sm" />
                            <TextInput label="Telefonnummer" placeholder="0213 247924792" required value={phoneNumber} onChange={e => setPhoneNumber(e.currentTarget.value)} mb="sm" />
                            <Textarea label="Beschreibung" placeholder="Zusätzliche Informationen" value={description} onChange={e => setDescription(e.currentTarget.value)} mb="sm" />
                            <Group   mt="md">
                                <Button variant={"filled"} type="submit" loading={loading}>Aktualisiere Vertrag</Button>
                            </Group>
                        </form>
                    </>
                )}
            </Drawer>
        </>

    );
};
